/**
 * 模块: DataCheck表格列配置
 * 职责: 定义数据检查表格的列结构
 * 限制: 文件<280行, 函数<30行
 */

import { ColumnsType } from 'antd/es/table';
import { DataRow, SKU } from './types';

/**
 * 获取唯一周期列表用于筛选
 */
function getPeriodFilters(data: DataRow[]) {
  const periods = Array.from(new Set(data.map(item => item.period_week)))
    .filter(Boolean)
    .sort();
  return periods.map(period => ({ text: period, value: period }));
}

/**
 * 获取唯一关键词列表用于筛选
 */
function getKeywordFilters(data: DataRow[]) {
  const keywords = Array.from(new Set(data.map(item => item.keyword)))
    .filter(Boolean)
    .sort();
  return keywords.map(keyword => ({ text: keyword, value: keyword }));
}

/**
 * 创建Top排名列配置
 */
function createTopColumns(rank: 1 | 2 | 3): ColumnsType<DataRow> {
  return [
    {
      title: `Top${rank} ASIN`,
      dataIndex: `top${rank}_asin`,
      key: `top${rank}_asin`,
      width: 120,
    },
    {
      title: `Top${rank} 品牌`,
      dataIndex: `top${rank}_brand`,
      key: `top${rank}_brand`,
      width: 120,
    },
    {
      title: `Top${rank} 点击份额(%)`,
      dataIndex: `top${rank}_click_share`,
      key: `top${rank}_click_share`,
      width: 140,
      align: 'right' as const,
      sorter: (a: DataRow, b: DataRow) => {
        const aVal = (a as any)[`top${rank}_click_share`] || 0;
        const bVal = (b as any)[`top${rank}_click_share`] || 0;
        return aVal - bVal;
      },
      render: (value: number) => value?.toFixed(2) || '0.00',
    },
    {
      title: `Top${rank} 转化份额(%)`,
      dataIndex: `top${rank}_conversion_share`,
      key: `top${rank}_conversion_share`,
      width: 140,
      align: 'right' as const,
      sorter: (a: DataRow, b: DataRow) => {
        const aVal = (a as any)[`top${rank}_conversion_share`] || 0;
        const bVal = (b as any)[`top${rank}_conversion_share`] || 0;
        return aVal - bVal;
      },
      render: (value: number) => value?.toFixed(2) || '0.00',
    },
  ];
}

/**
 * 创建表格列配置
 */
export function createColumns(skus: SKU[], data: DataRow[] = []): ColumnsType<DataRow> {
  return [
    {
      title: 'SKU',
      dataIndex: 'sku_name',
      key: 'sku_name',
      fixed: 'left' as const,
      width: 150,
      filters: skus.map(sku => ({ text: sku.name, value: sku.name })),
      onFilter: (value: any, record: DataRow) => record.sku_name === value,
      filterSearch: true,
    },
    {
      title: '周期',
      dataIndex: 'period_week',
      key: 'period_week',
      width: 120,
      sorter: (a: DataRow, b: DataRow) => a.period_week.localeCompare(b.period_week),
      filters: getPeriodFilters(data),
      onFilter: (value: any, record: DataRow) => record.period_week === value,
      filterSearch: true,
    },
    {
      title: '开始日期',
      dataIndex: 'period_start',
      key: 'period_start',
      width: 120,
      sorter: (a: DataRow, b: DataRow) => a.period_start.localeCompare(b.period_start),
    },
    {
      title: '结束日期',
      dataIndex: 'period_end',
      key: 'period_end',
      width: 120,
      sorter: (a: DataRow, b: DataRow) => a.period_end.localeCompare(b.period_end),
    },
    {
      title: '搜索词',
      dataIndex: 'keyword',
      key: 'keyword',
      width: 200,
      ellipsis: true,
      fixed: 'left' as const,
      filters: getKeywordFilters(data),
      onFilter: (value: any, record: DataRow) => record.keyword === value,
      filterSearch: true,
    },
    {
      title: '搜索频率排名',
      dataIndex: 'search_frequency_rank',
      key: 'search_frequency_rank',
      width: 130,
      align: 'right' as const,
      sorter: (a: DataRow, b: DataRow) => 
        (a.search_frequency_rank || 0) - (b.search_frequency_rank || 0),
    },
    ...createTopColumns(1),
    ...createTopColumns(2),
    ...createTopColumns(3),
  ];
}

